import React, { useEffect, useState } from "react";
import { fetchCandidateDetail, patchCandidate, fetchLinkedinExperience } from "../../../services/candidateService";
import { fetchClientPlacementDetail } from "../../../services/clientPlacementService";
import { CandidateDetail, LinkedInExperienceResponse, ClientPlacementDetailResponse } from "../../../types/fyo";
import { useAuth }                     from "../../../context/useAuth";
import PageHeader   from "../../shared/PageHeader/PageHeader";
import Badge        from "../../shared/Badge/Badge";
import { formatDateLong } from "../../../utils/formatters";
import "./CandidateDetailPage.css";

interface Props {
  candidateId: string;
  onBack:      () => void;
}

export default function CandidateDetailPage({ candidateId, onBack }: Props) {
  const { email: currentUser } = useAuth();
  const [candidate,      setCandidate]      = useState<CandidateDetail | null>(null);
  const [experience,     setExperience]     = useState<LinkedInExperienceResponse | null>(null);
  const [clientPlacement, setClientPlacement] = useState<ClientPlacementDetailResponse | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState<string | null>(null);

  // ── LinkedIn edit (info card) ─────────────────────────────
  const [editingLinkedin, setEditingLinkedin] = useState(false);
  const [linkedinValue,   setLinkedinValue]   = useState("");
  const [linkedinSaving,  setLinkedinSaving]  = useState(false);
  const [linkedinError,   setLinkedinError]   = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchCandidateDetail(candidateId)
      .then((c) => {
        setCandidate(c);
        setLinkedinValue(c.linkedin_url || "");
        return Promise.all([
          fetchLinkedinExperience(c.rms_candidate_id),
          fetchClientPlacementDetail(c.rms_candidate_id).catch((e: any) => {
            if (e.status === 404) return null;
            throw e;
          }),
        ]);
      })
      .then(([exp, cp]) => {
        setExperience(exp);
        setClientPlacement(cp);
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [candidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Info card LinkedIn ────────────────────────────────────
  const handleLinkedinSave = async () => {
    if (!candidate) return;
    setLinkedinSaving(true); setLinkedinError(null);
    try {
      await patchCandidate(candidate.rms_candidate_id, { linkedin_url: linkedinValue, changed_by: currentUser });
      setEditingLinkedin(false);
      load();
    } catch (e: any) { setLinkedinError(e.message); }
    finally { setLinkedinSaving(false); }
  };

  // ── Render ────────────────────────────────────────────────
  if (loading) return <div className="fyo-page"><p className="state-text state-text--muted">Loading...</p></div>;
  if (error)   return <div className="fyo-page"><div className="fyo-error-banner">{error}</div></div>;
  if (!candidate) return null;

  const c = candidate;

  return (
    <div className="fyo-page">
      <button className="fyo-back-btn" onClick={onBack}>← Back to Candidates</button>
      <PageHeader title={c.display_name} subtitle={`ID: ${c.rms_candidate_id}`} />

      {/* ── Candidate Info ── */}
      <div className="fyo-card">
        <h2 className="fyo-section-title">Candidate Info</h2>
        <div className="fyo-detail-grid">
          <LV label="Candidate ID"  value={String(c.rms_candidate_id)} />
          <LV label="Email"         value={c.email} />
          <LV label="Job Title"     value={c.job_title} />
          <LV label="Current Employer" value={c.current_employer} />
          <LV label="State"         value={c.state} />
          <LV label="Town"          value={c.town} />
          <LV label="Country"       value={c.country} />
          <LV label="Added"         value={formatDateLong(c.rms_created_at)} />
          <LV label="Last Updated"  value={formatDateLong(c.rms_last_updated)} />

          {/* LinkedIn — full width */}
          <div className="fyo-detail-field fyo-detail-field--wide">
            <span className="fyo-detail-field__label">LinkedIn</span>
            {editingLinkedin ? (
              <div className="fyo-inline-linkedin">
                <input
                  className="fyo-input"
                  style={{ flex: 1 }}
                  value={linkedinValue}
                  onChange={(e) => setLinkedinValue(e.target.value)}
                  placeholder="https://linkedin.com/in/..."
                  autoFocus
                />
                {linkedinError && <span className="text-danger" style={{ fontSize: "var(--font-size-sm)" }}>{linkedinError}</span>}
                <button className="fyo-btn fyo-btn--primary fyo-btn--sm"
                  disabled={linkedinSaving} onClick={handleLinkedinSave}>
                  {linkedinSaving ? "Saving..." : "Save"}
                </button>
                <button className="fyo-btn fyo-btn--ghost fyo-btn--sm"
                  onClick={() => { setEditingLinkedin(false); setLinkedinError(null); }}>Cancel</button>
              </div>
            ) : (
              <div className="fyo-detail-field__value-row">
                {c.linkedin_url
                  ? <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="fyo-link-btn">{c.linkedin_url}</a>
                  : <span className="fyo-muted">Not set</span>}
                <button className="fyo-btn fyo-btn--ghost fyo-btn--sm"
                  onClick={() => { setEditingLinkedin(true); setLinkedinValue(c.linkedin_url || ""); }}>
                  {c.linkedin_url ? "Edit" : "Add LinkedIn URL"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Influenced Relocation ── */}
      <div className="fyo-card">
        <h2 className="fyo-section-title">LinkedIn Tracked Ohio Relocations</h2>
        {c.ohio_move ? (
          <>
            <div className="fyo-status-row">
              <span className="fyo-status-row__label">Status</span>
              <Badge text={c.ohio_move.status} />
              {c.ohio_move.move_detected && (
                <span className="fyo-loop-closed">✓ Moved to Ohio</span>
              )}
            </div>
            <div className="fyo-detail-grid">
              <LV label="Baseline State"        value={c.ohio_move.rms_baseline_state} />
              <LV label="Tracking Start"            value={formatDateLong(c.ohio_move.tracking_window_start)} />
              <LV label="Tracking End"              value={formatDateLong(c.ohio_move.tracking_window_end)} />
              <LV
                label={c.ohio_move.status === "detected" ? "Ohio Location" : "Current LinkedIn Location"}
                value={c.ohio_move.status === "detected" ? c.ohio_move.raw_linkedin_ohio_location : c.ohio_move.linkedin_current_location}
              />
            </div>

          </>
        ) : (
          <div className="fyo-empty-card">No Ohio Move record for this candidate.</div>
        )}
      </div>

      {/* ── Influenced Placement ── */}
      <div className="fyo-card">
        <h2 className="fyo-section-title">LinkedIn Tracked Ohio Hires</h2>
        {c.ohio_employer ? (
          <>
            <div className="fyo-status-row">
              <span className="fyo-status-row__label">Status</span>
              <Badge text={c.ohio_employer.status} />
              {c.ohio_employer.status === "already_in_ohio" && (
                <span className="fyo-badge--preexisting">Pre-existing</span>
              )}
              {c.ohio_employer.status === "detected" && (
                <span className="fyo-loop-closed">✓ Ohio Employer Detected</span>
              )}
            </div>
            <div className="fyo-detail-grid">
              <LV label="Current Employer"       value={c.ohio_employer.rms_current_employer} />
              <LV label="Tracking Start"         value={formatDateLong(c.ohio_employer.tracking_window_start)} />
              <LV label="Tracking End"           value={formatDateLong(c.ohio_employer.tracking_window_end)} />
              <LV label="Ohio Employer Found"    value={c.ohio_employer.ohio_employer} />
              <LV label="Ohio Employer Location" value={c.ohio_employer.ohio_employer_location} />
            </div>

          </>
        ) : (
          <div className="fyo-empty-card">No Ohio Employer record for this candidate.</div>
        )}
      </div>

      {/* ── Direct Placement ──
      <div className="fyo-card">
        <h2 className="fyo-section-title">Direct Placement ({c.referrals.length})</h2>
        {c.referrals.length === 0 ? (
          <div className="fyo-empty-card">No referrals for this candidate.</div>
        ) : (
          <div className="fyo-table-wrap">
          <table className="fyo-table">
            <thead>
              <tr>
                <th>Referral ID</th>
                <th>Referred Company</th>
                <th>Referral Date</th>
                <th>Tracking Start</th>
                <th>Tracking End</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {c.referrals.map((r, i) => (
                <tr key={r.id} className={i % 2 === 1 ? "fyo-table__row--odd" : ""}>
                  <td>{r.opportunity_resource_id ?? "—"}</td>
                  <td className="fyo-table__cell--primary">{r.referred_company || "—"}</td>
                  <td>{formatDateLong(r.referral_date)}</td>
                  <td>{formatDateLong(r.tracking_window_start)}</td>
                  <td>{formatDateLong(r.tracking_window_end)}</td>
                  <td><Badge text={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      ── */}

      {/* ── Client Placement ── */}
      <div className="fyo-card">
        <h2 className="fyo-section-title">LinkedIn Tracked FYO Company Hires</h2>
        {clientPlacement ? (
          <>
            <div className="fyo-status-row">
              <span className="fyo-status-row__label">Status</span>
              <Badge text={clientPlacement.status} />
              {clientPlacement.status === "placed" && (
                <span className="fyo-loop-closed">✓ Hired in Ohio</span>
              )}
            </div>
            <div className="fyo-detail-grid">
              <LV label="Tracking Start"   value={formatDateLong(clientPlacement.tracking_window_start)} />
              <LV label="Tracking End"     value={formatDateLong(clientPlacement.tracking_window_end)} />
              <LV label="Matched Company"  value={clientPlacement.matched_company_name} />
              <LV label="Job Location"     value={clientPlacement.matched_job_location} />
            </div>

          </>
        ) : (
          <div className="fyo-empty-card">No Client Placement record for this candidate.</div>
        )}
      </div>

      {/* ── LinkedIn Experience ── */}
      <div className="fyo-card">
        <h2 className="fyo-section-title">LinkedIn Experience</h2>
        {!experience || experience.experience.length === 0 ? (
          <div className="fyo-empty-card">No LinkedIn experience scraped yet.</div>
        ) : (
          <div className="fyo-table-wrap">
          <table className="fyo-table fyo-table--compact">
            <thead>
              <tr>
                <th>Company</th>
                <th>Position</th>
                <th>Location</th>
                <th>Period</th>
              </tr>
            </thead>
            <tbody>
              {experience.experience.map((e, i) => {
                return (
                  <tr key={i} className={i % 2 === 1 ? "fyo-table__row--odd" : ""}>
                    <td className="fyo-table__cell--primary">{e.company}</td>
                    <td>{e.position}</td>
                    <td>{e.location || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {e.start_date || "?"} → {e.end_date || "Present"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

    </div>
  );
}

function LV({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="fyo-detail-field">
      <span className="fyo-detail-field__label">{label}</span>
      <span className="fyo-detail-field__value">{value || "—"}</span>
    </div>
  );
}
